// Central source of truth for all plan limits and feature flags

export type PlanTier = 'free' | 'starter' | 'pro' | 'annual';

export interface PlanLimits {
  name: string;
  emailsPerDay: number | null; // null = monthly limit instead
  emailsPerMonth: number | null; // null = daily limit instead
  emailsLifetime: number | null; // null = unlimited lifetime
  maxAccounts: number;
  unblurredEmails: number | 'all'; // number of emails shown unblurred, or 'all'
  followUpEnabled: boolean;
  followUpLimit: number | 'unlimited';
  followUpsPerDay: number | 'unlimited';
  followUpDelayDays: number;
  analyticsAccess: 'locked' | 'today_only' | 'full';
  analyticsBlur: 'all' | 'past_only' | 'none'; // 'all' = everything blurred, 'past_only' = today visible rest blurred
  templateRegenLimit: number | 'unlimited';
  jobApplyLimitPerDay: number | 'unlimited';
  jobReferralLimitPerDay: number | 'unlimited';
  priceINR: { monthly: number; yearly: number };
  priceUSD: { monthly: number; yearly: number };
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    name: 'Free',
    emailsPerDay: 10,
    emailsPerMonth: null,
    emailsLifetime: 70,
    maxAccounts: 1,
    unblurredEmails: 0,
    followUpEnabled: false,
    followUpLimit: 0,
    followUpsPerDay: 0,
    followUpDelayDays: 0,
    analyticsAccess: 'locked',
    analyticsBlur: 'all',
    templateRegenLimit: 2,
    jobApplyLimitPerDay: 5,
    jobReferralLimitPerDay: 3,
    priceINR: { monthly: 0, yearly: 0 },
    priceUSD: { monthly: 0, yearly: 0 },
  },
  starter: {
    name: 'Starter',
    emailsPerDay: 30,
    emailsPerMonth: 300,
    emailsLifetime: null,
    maxAccounts: 2,
    unblurredEmails: 5,
    followUpEnabled: true,
    followUpLimit: 100,
    followUpsPerDay: 5,
    followUpDelayDays: 4,
    analyticsAccess: 'today_only',
    analyticsBlur: 'past_only',
    templateRegenLimit: 'unlimited',
    jobApplyLimitPerDay: 15,
    jobReferralLimitPerDay: 12,
    priceINR: { monthly: 149, yearly: 0 },
    priceUSD: { monthly: 4.99, yearly: 0 },
  },
  pro: {
    name: 'Pro',
    emailsPerDay: 40,
    emailsPerMonth: 500,
    emailsLifetime: null,
    maxAccounts: 2,
    unblurredEmails: 'all',
    followUpEnabled: true,
    followUpLimit: 'unlimited',
    followUpsPerDay: 'unlimited',
    followUpDelayDays: 4,
    analyticsAccess: 'full',
    analyticsBlur: 'past_only',
    templateRegenLimit: 'unlimited',
    jobApplyLimitPerDay: 'unlimited',
    jobReferralLimitPerDay: 19,
    priceINR: { monthly: 299, yearly: 0 },
    priceUSD: { monthly: 9.99, yearly: 0 },
  },
  annual: {
    name: 'Annual',
    emailsPerDay: 40,
    emailsPerMonth: null,
    emailsLifetime: null,
    maxAccounts: 3,
    unblurredEmails: 'all',
    followUpEnabled: true,
    followUpLimit: 'unlimited',
    followUpsPerDay: 'unlimited',
    followUpDelayDays: 4,
    analyticsAccess: 'full',
    analyticsBlur: 'past_only',
    templateRegenLimit: 'unlimited',
    jobApplyLimitPerDay: 'unlimited',
    jobReferralLimitPerDay: 'unlimited',
    priceINR: { monthly: 0, yearly: 1999 },
    priceUSD: { monthly: 0, yearly: 79 },
  },
};

export function getUserPlanLimits(tier: string): PlanLimits {
  return PLAN_LIMITS[(tier as PlanTier)] || PLAN_LIMITS.free;
}

export function canConnectMoreAccounts(tier: string, currentCount: number): boolean {
  const limits = getUserPlanLimits(tier);
  return currentCount < limits.maxAccounts;
}

export function isSubscriptionActive(tier: string, expiresAt: string | null): boolean {
  if (tier === 'free') return true;
  if (!expiresAt) return false;
  return new Date(expiresAt) > new Date();
}

export function getEffectivePlan(tier: string, expiresAt: string | null): PlanTier {
  if (tier === 'free') return 'free';
  if (!expiresAt || new Date(expiresAt) <= new Date()) return 'free'; // expired
  return tier as PlanTier;
}

// Fun upgrade captions per country
export function getUpgradeCaption(country: string, tier: string): string {
  if (tier === 'starter') {
    return "Starter plan active";
  }
  if (tier === 'pro') {
    return "Pro plan active";
  }
  if (tier === 'annual') {
    return "Annual plan active";
  }
  return "Invest in your career today!";
}
